/**
 * Field Value Resolver
 *
 * Resolves field values from records by trying multiple field name formats.
 * Gobodo returns lowercase field names (e.g., "name", "email") but code may
 * expect Salesforce format (e.g., "Name__c", "Email__c").
 */

function getFlatRecord(record: Record<string, any>): Record<string, any> {
  if (!record) return {};
  const flat = { ...record };
  const nested = record.data ?? record.properties;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    Object.assign(flat, nested);
  }
  return flat;
}

/**
 * If value is a Gobodo lookup/relationship object, return display label or key for list display.
 */
function unwrapRelationshipValue(value: any): any {
  if (value == null || typeof value !== 'object') return value;
  const ro = value?.relatedObject ?? value;
  if (ro && typeof ro === 'object') {
    const label = ro.label ?? ro.name ?? ro.key;
    if (label != null && label !== '') return String(label);
    if (ro.key != null) return String(ro.key);
    if (ro.id != null) return String(ro.id);
  }
  return value;
}

/**
 * Resolve field value from record by trying multiple key formats.
 * Handles Gobodo __c fields, nested data/properties, camelCase/snake_case, and custom suffix (__stg_c).
 */
export function resolveFieldValue(record: Record<string, any>, fieldApiName: string): any {
  if (!record || !fieldApiName) return undefined;

  const flat = getFlatRecord(record);

  let val = flat.hasOwnProperty(fieldApiName) ? flat[fieldApiName] : undefined;
  const lowerKey = fieldApiName.toLowerCase();
  if (val === undefined && flat.hasOwnProperty(lowerKey)) val = flat[lowerKey];

  if (val === undefined && !fieldApiName.endsWith('__c')) {
    const withSuffix = `${fieldApiName}__c`;
    if (flat.hasOwnProperty(withSuffix)) val = flat[withSuffix];
    if (val === undefined) {
      const lowerWithSuffix = `${lowerKey}__c`;
      if (flat.hasOwnProperty(lowerWithSuffix)) val = flat[lowerWithSuffix];
    }
  }

  if (val === undefined && fieldApiName.endsWith('__c')) {
    const withoutSuffix = fieldApiName.slice(0, -3);
    if (flat.hasOwnProperty(withoutSuffix)) val = flat[withoutSuffix];
    if (val === undefined) {
      const lowerWithoutSuffix = withoutSuffix.toLowerCase();
      if (flat.hasOwnProperty(lowerWithoutSuffix)) val = flat[lowerWithoutSuffix];
    }
  }

  if (val === undefined) {
    // Match keys with __c or custom suffix (e.g. __stg_c, __stg3_c) so Name__stg_c matches 'name'
    const norm = (k: string) =>
      k.toLowerCase().replace(/__stg3_c$|__stg_c$|__c$/i, '');
    const fieldNorm = norm(fieldApiName);
    const matchingKey = Object.keys(flat).find(key => norm(key) === fieldNorm);
    if (matchingKey) val = flat[matchingKey];
  }

  if (val === undefined) {
    const exactLower = Object.keys(flat).find(key => key.toLowerCase() === lowerKey);
    if (exactLower) val = flat[exactLower];
  }

  return unwrapRelationshipValue(val);
}
